import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag } from 'lucide-react';
import { formatPrice } from '../../utils/priceUtils';

interface CheckoutShippingStepProps {
    availableRates: any[];
    selectedShippingId: string | null;
    setSelectedShippingId: (id: string) => void;
    totalAmount: number;
    commissionPct: number;
    minFee: number;
    currency: string;
    addressData?: { city: string, country: string };
    shippingOrigin?: string;
}

const CheckoutShippingStep: React.FC<CheckoutShippingStepProps> = ({
    availableRates,
    selectedShippingId,
    setSelectedShippingId,
    totalAmount,
    commissionPct,
    minFee,
    currency,
    addressData,
    shippingOrigin
}) => {
    const { t } = useTranslation('marketplace');

    return (
        <div className="space-y-6">
            {addressData && (
                <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                    <div className="flex-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Expédition</p>
                        <p className="text-sm font-bold text-gray-700">
                            {shippingOrigin || "Centre de distribution"}
                        </p>
                    </div>
                    <div className="flex-1 text-right">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Destination</p>
                        <p className="text-sm font-bold text-gray-700">
                            {addressData.city}, {addressData.country}
                        </p>
                    </div>
                </div>
            )}

            {availableRates.length > 0 ? (
                <div className="space-y-2">
                    {availableRates.map(rate => {
                        const isSelected = selectedShippingId === rate.id;
                        const isFree = rate.free_threshold && totalAmount >= rate.free_threshold;
                        const missingForFree = rate.free_threshold && !isFree ? rate.free_threshold - totalAmount : 0;
                        const progress = rate.free_threshold ? Math.min(100, (totalAmount / rate.free_threshold) * 100) : 0;

                        return (
                            <button
                                key={rate.id}
                                type="button"
                                onClick={() => setSelectedShippingId(rate.id)}
                                className={`w-full text-left relative overflow-hidden transition-all duration-200 rounded-2xl border-2 p-4 group ${isSelected ? 'border-purple-600 bg-purple-50' : 'border-gray-100 hover:border-purple-200'}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div>
                                        <p className="font-bold text-gray-900 group-hover:text-purple-700 transition-colors">{rate.name}</p>
                                        <p className="text-xs text-gray-500">{rate.estimated_days}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-gray-900">{isFree ? <span className="text-green-600">{t('checkout.orders.free')}</span> : formatPrice(rate.price, currency)}</p>
                                        {rate.free_threshold > 0 && isFree && (
                                            <p className="text-[10px] font-bold text-green-600 uppercase tracking-wider">{t('checkout.orders.threshold_reached')}</p>
                                        )}
                                    </div>
                                </div>

                                {missingForFree > 0 && (
                                    <div className="mt-3 bg-white/50 p-2 rounded-lg border border-purple-100">
                                        <div className="flex justify-between items-end mb-1">
                                            <p className="text-[10px] font-bold text-purple-600 uppercase">
                                                {t('checkout.orders.missing_for_free', { amount: formatPrice(missingForFree, currency) })}
                                            </p>
                                            <span className="text-[10px] font-bold text-gray-400">{Math.round(progress)}%</span>
                                        </div>
                                        <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="p-8 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100">
                    <ShoppingBag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">{t('common:common.no_shipping_available') || "Aucun mode de livraison disponible."}</p>
                </div>
            )}

            <div className="p-6 bg-purple-50 rounded-2xl border border-purple-100 space-y-2">
                <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>{t('cart.subtotal')}</span>
                    <span>{formatPrice(totalAmount, currency)}</span>
                </div>
                {(() => {
                    const rate = availableRates.find(r => r.id === selectedShippingId);
                    const shipping = rate ? (rate.free_threshold && totalAmount >= rate.free_threshold ? 0 : rate.price) : 0;
                    const calculatedCommission = Math.round(totalAmount * (commissionPct / 100));
                    const commission = calculatedCommission + (minFee * 100);
                    const totalOrder = totalAmount + commission + shipping;

                    return (
                        <>
                            {commission > 0 && (
                                <div className="flex justify-between text-xs text-gray-500 font-medium italic">
                                    <span>{t('cart.service_fee', { percent: commissionPct })}</span>
                                    <span>{formatPrice(commission, currency)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-xs text-gray-500 font-medium italic">
                                <span className="capitalize">{t('cart.shipping')}</span>
                                <span>{shipping === 0 ? t('checkout.orders.free') : formatPrice(shipping, currency)}</span>
                            </div>
                            <div className="pt-2 border-t border-purple-200 flex justify-between font-black text-purple-900 text-lg">
                                <span>{t('checkout.total')}</span>
                                <span>{formatPrice(totalOrder, currency)}</span>
                            </div>
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default CheckoutShippingStep;
